import { COLOR } from 'config/styled-components-colors';
import { ThemeColor } from 'config/styled-components-styles';
import styled from 'styled-components';

const DEFAULT_HEADING_H1_COLOR: ThemeColor = 'luckyPoint';
const DEFAULT_HEADING_COLOR: ThemeColor = 'manatee';
const DEFAULT_HEADING_FONT_WEIGHT = 700;
const DEFAULT_HEADING_H1_FONT_SIZE = 24;
const DEFAULT_HEADING_H2_FONT_SIZE = 20;
const DEFAULT_HEADING_H3_FONT_SIZE = 16;
const DEFAULT_HEADING_H1_LINE_HEIGHT = '32px';
const DEFAULT_HEADING_H2_LINE_HEIGHT = '24px';
const DEFAULT_HEADING_H3_LINE_HEIGHT = '24px';
const DEFAULT_TEXT_COLOR: ThemeColor = 'ebonyClay';
const DEFAULT_PARAGRAPH_COLOR: ThemeColor = 'manatee';
const DEFAULT_TEXT_FONT_SIZE = 16;
const DEFAULT_FONT_WEIGHT = 500;
const DEFAULT_TEXT_LINE_HEIGHT = 24;
const DEFAULT_TEXT_OVERFLOW = 'visible';
const DEFAULT_TEXT_TEXT_OVERFLOW = 'clip';
const DEFAULT_TEXT_ALIGN = 'start';
const DEFAULT_TEXT_TRANSFORM = 'none';
const DEFAULT_TEXT_DECORATION = 'none';
const DEFAULT_TEXT_WHITE_SPACE = 'normal';
const DEFAULT_TEXT_WORD_BREAK = 'normal';
const DEFAULT_TEXT_LINE_BREAK = 'auto';
const DEFAULT_TEXT_WORD_WRAP = 'normal';
const DEFAULT_TEXT_DISPLAY = 'inline';
const DEFAULT_TEXT_ALIGN_ITEMS = 'unset';
const DEFAULT_TEXT_HEIGHT = 'unset';
const DEFAULT_TEXT_MAX_WIDTH = 'unset';
const DEFAULT_TEXT_MIN_WIDTH = 'unset';
const DEFAULT_TEXT_PADDING = 'unset';
const DEFAULT_TEXT_MARGIN = 'unset';
const DEFAULT_PARAGRAPH_FONT_SIZE = 11;
const DEFAULT_PARAGRAPH_LINE_HEIGHT = 20;
const DEFAULT_TEXT_OPACITY = 1;
const DEFAULT_TEXT_USER_SELECT = 'auto';
const DEFAULT_TEXT_LETTER_SPACING = 'normal';

type Props = {
    $color?: ThemeColor;
    $fontWeight?: number;
    $fontSize?: number;
    $lineHeight?: string;
};

export const H1 = styled.h1<Props>`
    color: ${(props): string => COLOR[props.$color ?? DEFAULT_HEADING_H1_COLOR]};
    line-height: ${(props): string => props.$lineHeight ?? DEFAULT_HEADING_H1_LINE_HEIGHT};
    font-weight: ${(props): number => props.$fontWeight ?? DEFAULT_HEADING_FONT_WEIGHT};
    font-size: ${(props): number => props.$fontSize ?? DEFAULT_HEADING_H1_FONT_SIZE}px;
`;

export const H2 = styled.h2<Props>`
    color: ${(props): string => COLOR[props.$color ?? DEFAULT_HEADING_COLOR]};
    line-height: ${(props): string => props.$lineHeight ?? DEFAULT_HEADING_H2_LINE_HEIGHT};
    font-weight: ${(props): number => props.$fontWeight ?? DEFAULT_HEADING_FONT_WEIGHT};
    font-size: ${(props): number => props.$fontSize ?? DEFAULT_HEADING_H2_FONT_SIZE}px;
`;

export const H3 = styled.h3<Props>`
    color: ${(props): string => COLOR[props.$color ?? DEFAULT_HEADING_COLOR]};
    line-height: ${(props): string => props.$lineHeight ?? DEFAULT_HEADING_H3_LINE_HEIGHT};
    font-weight: ${(props): number => props.$fontWeight ?? DEFAULT_HEADING_FONT_WEIGHT};
    font-size: ${(props): number => props.$fontSize ?? DEFAULT_HEADING_H3_FONT_SIZE}px;
`;

type TextProps = {
    $color?: ThemeColor;
    $fontSize?: number;
    $fontWeight?: number;
    $lineHeight?: number;
    $textAlign?: 'center' | 'start' | 'end';
    $overflow?: 'visible' | 'hidden';
    $textOverflow?: 'ellipsis' | 'clip';
    $textTransform?: 'none' | 'uppercase' | 'capitalize' | 'lowercase';
    $textDecoration?: 'none' | 'overline' | 'underline' | 'line-through' | 'underline overline';
    $whiteSpace?: 'normal' | 'nowrap' | 'pre-wrap' | 'pre-line' | 'break-spaces';
    $wordBreak?: 'normal' | 'break-all' | 'keep-all' | 'break-word';
    $lineBreak?: 'auto' | 'anywhere' | 'loose' | 'normal' | 'strict';
    $wordWrap?: 'normal' | 'break-word';
    $opacity?: number;
    $margin?: string;
    $userSelect?: string;
    $cursor?: string;
    $letterSpacing?: string;
    $display?: 'block' | 'inline-block' | 'inline' | 'inline-flex' | 'contents';
    $alignItems?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
    $height?: string;
    $maxWidth?: string;
    $minWidth?: string;
    $padding?: string;
};

export const Text = styled.span<TextProps>`
    color: ${(props): string => COLOR[props.$color ?? DEFAULT_TEXT_COLOR]};
    font-size: ${(props): number => props.$fontSize ?? DEFAULT_TEXT_FONT_SIZE}px;
    font-weight: ${(props): number => props.$fontWeight ?? DEFAULT_FONT_WEIGHT};
    letter-spacing: 0;
    line-height: ${(props): number => props.$lineHeight ?? DEFAULT_TEXT_LINE_HEIGHT}px;
    overflow: ${(props): string => props.$overflow ?? DEFAULT_TEXT_OVERFLOW};
    text-overflow: ${(props): string => props.$textOverflow ?? DEFAULT_TEXT_TEXT_OVERFLOW};
    text-align: ${(props): string => props.$textAlign ?? DEFAULT_TEXT_ALIGN};
    text-transform: ${(props): string => props.$textTransform ?? DEFAULT_TEXT_TRANSFORM};
    text-decoration: ${(props): string => props.$textDecoration ?? DEFAULT_TEXT_DECORATION};
    white-space: ${(props): string => props.$whiteSpace ?? DEFAULT_TEXT_WHITE_SPACE};
    word-break: ${(props): string => props.$wordBreak ?? DEFAULT_TEXT_WORD_BREAK};
    line-break: ${(props): string => props.$lineBreak ?? DEFAULT_TEXT_LINE_BREAK};
    word-wrap: ${(props): string => props.$wordWrap ?? DEFAULT_TEXT_WORD_WRAP};
    opacity: ${(props): number => props.$opacity ?? DEFAULT_TEXT_OPACITY};
    margin: ${(props): string => props.$margin ?? DEFAULT_TEXT_MARGIN};
    user-select: ${(props): string => props.$userSelect ?? DEFAULT_TEXT_USER_SELECT};
    letter-spacing: ${(props): string => props.$letterSpacing ?? DEFAULT_TEXT_LETTER_SPACING};
    display: ${(props): string => props.$display ?? DEFAULT_TEXT_DISPLAY};
    align-items: ${(props): string => props.$alignItems ?? DEFAULT_TEXT_ALIGN_ITEMS};
    height: ${(props): string => props.$height ?? DEFAULT_TEXT_HEIGHT};
    max-width: ${(props): string => props.$maxWidth ?? DEFAULT_TEXT_MAX_WIDTH};
    min-width: ${(props): string => props.$minWidth ?? DEFAULT_TEXT_MIN_WIDTH};
    padding: ${(props): string => props.$padding ?? DEFAULT_TEXT_PADDING};
    cursor: ${(props): string => {
        if (props.$cursor) return props.$cursor;
        return props.onClick ? 'pointer' : 'default';
    }};
`;

type TextPreformattedProps = {
    $color?: ThemeColor;
    $fontSize?: number;
    $fontWeight?: number;
    $lineHeight?: number;
    $textAlign?: 'center' | 'start' | 'end';
    $overflow?: 'visible' | 'hidden';
    $textOverflow?: 'ellipsis' | 'clip';
    $textTransform?: 'none' | 'uppercase' | 'capitalize' | 'lowercase';
    $textDecoration?: 'none' | 'overline' | 'underline' | 'line-through' | 'underline overline';
    $wordBreak?: 'normal' | 'break-all' | 'keep-all';
    $opacity?: number;
    $margin?: string;
    $userSelect?: string;
    $cursor?: string;
};

export const TextPreformatted = styled.pre<TextPreformattedProps>`
    color: ${(props): string => COLOR[props.$color ?? DEFAULT_TEXT_COLOR]};
    font-size: ${(props): number => props.$fontSize ?? DEFAULT_TEXT_FONT_SIZE}px;
    font-weight: ${(props): number => props.$fontWeight ?? DEFAULT_FONT_WEIGHT};
    letter-spacing: 0;
    line-height: ${(props): number => props.$lineHeight ?? DEFAULT_TEXT_LINE_HEIGHT}px;
    overflow: ${(props): string => props.$overflow ?? DEFAULT_TEXT_OVERFLOW};
    text-overflow: ${(props): string => props.$textOverflow ?? DEFAULT_TEXT_TEXT_OVERFLOW};
    text-align: ${(props): string => props.$textAlign ?? DEFAULT_TEXT_ALIGN};
    text-transform: ${(props): string => props.$textTransform ?? DEFAULT_TEXT_TRANSFORM};
    text-decoration: ${(props): string => props.$textDecoration ?? DEFAULT_TEXT_DECORATION};
    word-break: ${(props): string => props.$wordBreak ?? DEFAULT_TEXT_WORD_BREAK};
    opacity: ${(props): number => props.$opacity ?? DEFAULT_TEXT_OPACITY};
    margin: ${(props): string => props.$margin ?? DEFAULT_TEXT_MARGIN};
    user-select: ${(props): string => props.$userSelect ?? DEFAULT_TEXT_USER_SELECT};
    cursor: ${(props): string => {
        if (props.$cursor) return props.$cursor;
        return props.onClick ? 'pointer' : 'default';
    }};
`;

type TextLineClampProps = {
    $lineClamp?: number;
};

export const TextLineClamp = styled(Text)<TextLineClampProps>`
    display: -webkit-box;
    -webkit-line-clamp: ${(props): number => props.$lineClamp ?? 1};
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

type ParagraphProps = {
    $color?: ThemeColor;
    $marginBottom?: number;
    $fontSize?: number;
    $lineHeight?: number;
    $fontWeight?: number;
};

export const Paragraph = styled.p<ParagraphProps>`
    color: ${(props): string => COLOR[props.$color ?? DEFAULT_PARAGRAPH_COLOR]};
    font-size: ${(props): number => props.$fontSize ?? DEFAULT_PARAGRAPH_FONT_SIZE}px;
    letter-spacing: 0;
    line-height: ${(props): number => props.$lineHeight ?? DEFAULT_PARAGRAPH_LINE_HEIGHT}px;
    margin-bottom: ${(props): number => props.$marginBottom ?? 12}px;
    font-weight: ${(props): number => props.$fontWeight ?? DEFAULT_FONT_WEIGHT};
`;
